var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHttp } from '../../../hooks/useHttp';
import { useMessage } from '../../../hooks/useMessage';
import styles from './tkSettings.module.scss';
/**
 * Величины расчёта текущего контроля.
 *
 * Здесь остались только те, которых нет в шкале оценки. Сколько у дисциплины
 * модулей и сколько баллов даёт каждый, решает её шкала (ebe_var): у одной
 * два модуля по 20 баллов, у другой один на 40. Раньше это были общие на вуз
 * настройки — они отменены, и держать их рядом было бы враньём.
 *
 * Отдельным блоком, а не строкой в общей таблице настроек выше, потому что та
 * приходит из вузовской процедуры SP_jurnal_settings с закрытым списком кодов:
 * модуль эталонов процедур не возит и дописать себя туда не вправе.
 */
const TkSettings = () => {
    var _a, _b, _c, _d, _e;
    const { t } = useTranslation();
    const { request } = useHttp();
    const toast = useMessage();
    const [rows, setRows] = React.useState([]);
    const [busy, setBusy] = React.useState('');
    const load = () => __awaiter(void 0, void 0, void 0, function* () {
        const { data } = yield request('/teacher/tk-settings');
        setRows(Array.isArray(data) ? data : []);
    });
    React.useEffect(() => {
        load();
    }, []);
    const at = (code) => rows.find(x => x.setting === code);
    const on = Boolean(Number((_a = at('modul')) === null || _a === void 0 ? void 0 : _a.on_off));
    const mark = Number((_b = at('tk_mark')) === null || _b === void 0 ? void 0 : _b.field) || 5;
    const countEmpty = Boolean(Number((_c = at('tk_empty')) === null || _c === void 0 ? void 0 : _c.on_off));
    const byHours = Boolean(Number((_d = at('tk_split')) === null || _d === void 0 ? void 0 : _d.on_off));
    /** Пишем по коду: номера строк у вузов разные, код один. */
    const save = (code, field, onOff) => __awaiter(void 0, void 0, void 0, function* () {
        var _f;
        setBusy(code);
        const res = yield request('/teacher/tk-settings', 'PUT', { setting: code, field, on_off: onOff });
        setBusy('');
        if ((res === null || res === void 0 ? void 0 : res.type) !== 'success' || !((_f = res === null || res === void 0 ? void 0 : res.data) === null || _f === void 0 ? void 0 : _f.updated)) {
            return toast((res === null || res === void 0 ? void 0 : res.message) || String(t('error_generic')), 'error');
        }
        setRows(prev => prev.map(x => (x.setting === code ? Object.assign(Object.assign({}, x), { field, on_off: onOff }) : x)));
        toast(String(t('saved')), 'success');
    });
    /** Поле числа: сохраняем по уходу из поля, как в остальных таблицах журнала. */
    const numberRow = (code, label, hint, min, max_) => {
        const row = at(code);
        if (!row)
            return null;
        return (_jsxs("div", Object.assign({ className: styles.row }, { children: [_jsxs("div", Object.assign({ className: styles.text }, { children: [_jsx("div", Object.assign({ className: styles.label }, { children: label })), _jsx("div", Object.assign({ className: styles.hint }, { children: hint }))] })), _jsx("input", { id: `tk-${code}`, className: styles.num, type: "number", min: min, max: max_, step: "1", disabled: busy === code, defaultValue: row.field, onBlur: e => {
                        const v = Number(e.target.value);
                        if (!Number.isFinite(v) || v < min || v > max_) {
                            e.target.value = String(row.field);
                            return;
                        }
                        if (v !== Number(row.field))
                            save(code, v, row.on_off);
                    } }, code + '-' + row.field)] }), code));
    };
    const switchRow = (code, label, hint, checked, field) => {
        const row = at(code);
        if (!row)
            return null;
        return (_jsxs("div", Object.assign({ className: styles.row }, { children: [_jsxs("div", Object.assign({ className: styles.text }, { children: [_jsx("div", Object.assign({ className: styles.label }, { children: label })), _jsx("div", Object.assign({ className: styles.hint }, { children: hint }))] })), _jsxs("label", Object.assign({ className: styles.switch }, { children: [_jsx("input", { id: `tk-${code}`, type: "checkbox", checked: checked, disabled: busy === code, onChange: e => save(code, field, e.target.checked ? 1 : 0) }), _jsx("span", {})] }))] }), code));
    };
    if (!rows.length)
        return null;
    return (_jsxs("section", Object.assign({ className: styles.block }, { children: [_jsxs("header", Object.assign({ className: styles.head }, { children: [_jsx("h4", { children: t('tk_title') }), _jsx("p", { children: t('tk_intro') })] })), switchRow('modul', String(t('tk_on')), String(t('tk_on_hint')), on, Number((_e = at('modul')) === null || _e === void 0 ? void 0 : _e.field) || 0), _jsxs("div", Object.assign({ className: on ? '' : styles.dim }, { children: [numberRow('tk_mark', String(t('tk_mark')), String(t('tk_mark_hint')), 1, 100), switchRow('tk_empty', String(t('tk_empty')), String(t('tk_empty_hint')), countEmpty, 0), switchRow('tk_split', String(t('tk_split')), String(t('tk_split_hint')), byHours, 0), _jsxs("div", Object.assign({ className: styles.formulas }, { children: [_jsx("div", { children: _jsx("code", { children: t('tk_formula', { mark }) }) }), _jsxs("div", { children: [_jsx("span", Object.assign({ className: styles.when }, { children: t('tk_example') })), _jsx("code", { children: t('tk_f1', { k: String(Number((40 / mark).toFixed(3))).replace('.', ','), cap: 40 }) })] }), _jsxs("div", { children: [_jsx("span", { className: styles.when }), _jsx("code", { children: t('tk_f2', { k: String(Number((20 / mark).toFixed(3))).replace('.', ','), cap: 20 }) })] })] }))] }))] })));
};
export default TkSettings;
